/**
 * SubscriptionBadge - Tier indicator badge
 */

import { Link } from 'react-router-dom';
import { cn } from '@/utils/cn';
import { useSubscriptionStore, selectTier } from '@/stores';
import type { SubscriptionTier } from '@/types/subscription';

interface SubscriptionBadgeProps {
  showUpgrade?: boolean;
  size?: 'sm' | 'md';
}

const tierStyles: Record<SubscriptionTier, { bg: string; text: string; border: string }> = {
  free: {
    bg: 'bg-slate-600/20',
    text: 'text-slate-400',
    border: 'border-slate-600/30',
  },
  pro: {
    bg: 'bg-hud-primary/20',
    text: 'text-hud-primary',
    border: 'border-hud-primary/30',
  },
  enterprise: {
    bg: 'bg-hud-secondary/20',
    text: 'text-hud-secondary',
    border: 'border-hud-secondary/30',
  },
};

const tierLabels: Record<SubscriptionTier, string> = {
  free: 'Free',
  pro: 'Pro',
  enterprise: 'Enterprise',
};

export function SubscriptionBadge({ showUpgrade = true, size = 'md' }: SubscriptionBadgeProps) {
  const tier = useSubscriptionStore(selectTier);
  const styles = tierStyles[tier];

  const sizeClasses = size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-3 py-1 text-sm';

  return (
    <div className="flex items-center gap-2">
      <span
        className={cn(
          'rounded-full font-medium border',
          sizeClasses,
          styles.bg,
          styles.text,
          styles.border
        )}
      >
        {tierLabels[tier]}
      </span>

      {showUpgrade && tier === 'free' && (
        <Link
          to="/pricing"
          className={cn(
            'rounded-full font-medium border transition-colors',
            sizeClasses,
            'bg-hud-primary/10 text-hud-primary border-hud-primary/30',
            'hover:bg-hud-primary/20'
          )}
        >
          Upgrade
        </Link>
      )}
    </div>
  );
}
